import { useState, useEffect, useRef } from 'react';
import { Settings, Moon, Sun, Bell, Mail, X, Type, Building } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@/lib/i18n';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/lib/ThemeContext';

export default function SettingsMenu() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { themeMode, setThemeMode } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [darkMode, setDarkMode] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('theme') === 'dark' ||
                (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
        }
        return false;
    });

    // Email: Synced with DB if logged in, else local
    const [emailEnabled, setEmailEnabled] = useState(true);
    const [fontSize, setFontSize] = useState(() => localStorage.getItem('font-size') || 'normal');
    const [savingEmail, setSavingEmail] = useState(false);

    const menuRef = useRef<HTMLDivElement>(null);

    // Initial load of email pref from profile
    useEffect(() => {
        if (user && user.id) {
            fetchEmailPref(user.id);
        } else {
            // Fallback to local
            setEmailEnabled(localStorage.getItem('email_notifications') !== 'false');
        }
    }, [user]);

    const fetchEmailPref = async (userId: string | undefined) => {
        if (!userId) return;
        const { data } = await supabase
            .from('profiles')
            .select('email_notifications')
            .eq('id', userId)
            .maybeSingle();
        if (data) {
            // If explicitly false, set false. Standard is true.
            setEmailEnabled(data.email_notifications !== false);
        }
    };

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    }, [darkMode]);

    useEffect(() => {
        document.documentElement.setAttribute('data-font-size', fontSize);
        localStorage.setItem('font-size', fontSize);
    }, [fontSize]);

    // Save email pref on change
    const toggleEmail = async () => {
        const newValue = !emailEnabled;
        setEmailEnabled(newValue);
        localStorage.setItem('email_notifications', String(newValue));

        if (user) {
            setSavingEmail(true);
            await supabase
                .from('profiles')
                .update({ email_notifications: newValue })
                .eq('id', user.id);
            setSavingEmail(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleDarkMode = () => setDarkMode(!darkMode);

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-all hover:scale-110 active:scale-95"
                aria-label={t('settings.title')}
            >
                <Settings className={`w-5 h-5 transition-transform duration-500 ${isOpen ? 'rotate-90' : ''}`} />
            </button>

            {isOpen && (
                <div className="fixed inset-0 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 z-[100] animate-scale-in origin-top-right">
                    {/* Backdrop for mobile */}
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm sm:hidden" onClick={() => setIsOpen(false)} />

                    <div className="relative bg-white dark:bg-slate-900 w-full sm:w-72 sm:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden h-full sm:h-auto flex flex-col sm:block">
                        {/* Mobile Header */}
                        <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-800 sm:hidden">
                            <h2 className="text-lg font-bold dark:text-white">{t('settings.title')}</h2>
                            <button onClick={() => setIsOpen(false)} className="p-2 text-slate-400">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-2 space-y-1">
                            {/* Dark Mode Toggle */}
                            <div className="p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-xl transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}>
                                        {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold dark:text-white">{t('settings.dark_mode')}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={toggleDarkMode}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${darkMode ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${darkMode ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>

                            {/* v2 Design Toggle */}
                            <div className="p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-xl transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${themeMode === 'v2' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}>
                                        <span className="text-[10px] font-bold">V2</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold dark:text-white">V2 Design (Elite)</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setThemeMode(themeMode === 'v2' ? 'classic' : 'v2')}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${themeMode === 'v2' ? 'bg-slate-800' : 'bg-slate-200 dark:bg-slate-700'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${themeMode === 'v2' ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>

                            {/* Kommuneportal Link */}
                            <div className="p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-xl transition-colors">
                                <Link to="/kommune" className="flex items-center gap-3 w-full" onClick={() => setIsOpen(false)}>
                                    <div className="p-2 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
                                        <Building className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold dark:text-white">For kommuner (SaaS)</p>
                                    </div>
                                </Link>
                            </div>

                            <div className="h-px bg-slate-100 dark:bg-slate-800 my-1 mx-3" />

                            <div className="px-3 py-2">
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">{t('settings.notifications')}</h3>

                                {/* Push Notifications (Disabled) */}
                                <div className="flex items-center justify-between py-2 text-slate-400 rounded-lg px-1 transition-colors cursor-not-allowed opacity-60">
                                    <div className="flex items-center gap-3">
                                        <Bell className="w-4 h-4 text-slate-400" />
                                        <span className="text-sm">{t('settings.push_notifications')}</span>
                                    </div>
                                    <div className="relative inline-flex h-5 w-9 items-center rounded-full bg-slate-200">
                                        <span className="inline-block h-3 w-3 transform rounded-full bg-white translate-x-1" />
                                    </div>
                                </div>

                                {/* Email Notifications (Active) */}
                                <div className="flex items-center justify-between py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg px-1 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-4 h-4 text-slate-400" />
                                        <span className="text-sm dark:text-slate-300">{t('settings.email_notifications')}</span>
                                    </div>
                                    <button
                                        onClick={toggleEmail}
                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${emailEnabled ? 'bg-green-500' : 'bg-slate-200 dark:bg-slate-700'}`}
                                        disabled={savingEmail}
                                    >
                                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform ${emailEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                                    </button>
                                </div>

                                <div className="h-px bg-slate-100 dark:bg-slate-800 my-2 mx-1" />

                                {/* Font Size Selection */}
                                <div className="px-1 py-1">
                                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">{t('settings.font_size')}</h3>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { id: 'normal', label: t('settings.font_normal') },
                                            { id: 'large', label: t('settings.font_large') },
                                            { id: 'xl', label: t('settings.font_extra_large') }
                                        ].map((size) => (
                                            <button
                                                key={size.id}
                                                onClick={() => setFontSize(size.id)}
                                                className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all ${fontSize === size.id
                                                    ? 'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-900/40 dark:border-blue-800'
                                                    : 'bg-slate-50 border-slate-100 text-slate-500 dark:bg-slate-800/40 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    }`}
                                            >
                                                <Type className={`
                                                    ${size.id === 'normal' ? 'w-3.5 h-3.5' : size.id === 'large' ? 'w-4.5 h-4.5' : 'w-5.5 h-5.5'}
                                                `} />
                                                <span className="text-[10px] font-medium">{size.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="h-px bg-slate-100 dark:bg-slate-800 my-1 mx-3" />

                            {/* Debug / clear queue */}
                            <div className="px-3 py-2">
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Feilsøking</h3>
                                <button
                                    onClick={async () => {
                                        if (window.confirm('Dette vil slette alle saker som venter på opplasting. Er du sikker?')) {
                                            try {
                                                // Dynamic import to avoid circular dependencies if any
                                                const { getAllPendingReports, removePendingReport } = await import('@/lib/offlineStorage');
                                                const reports = await getAllPendingReports();
                                                for (const r of reports) {
                                                    await removePendingReport(r.id);
                                                }
                                                window.location.reload();
                                            } catch (e) {
                                                console.error(e);
                                                alert('Kunne ikke tømme køen.');
                                            }
                                        }
                                    }}
                                    className="w-full text-left px-2 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                    Tøm opplastingskø (Reset)
                                </button>
                            </div>
                        </div>

                        {/* Footer info */}
                        <div className="mt-auto sm:mt-0 p-4 bg-slate-50 dark:bg-slate-800/50 sm:rounded-b-2xl border-t border-slate-100 dark:border-slate-800">
                            <p className="text-[10px] text-slate-400 text-center uppercase tracking-widest flex items-center justify-center gap-2">
                                TN v1.0.4 • Norway
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
